import { Button, Form, Input, InputNumber, Space } from "antd";
import { useEffect, useRef, useState } from "react";
import { GOOGLE_API_KEY } from "../../constants/constants";
import { toast } from "../../lib/toast";
import { ModalComponentProps } from "../../types";
import CustomModal from "../ui/CustomModal";
import Loader from "../ui/Loader";

// export interface IGeoLocation {
//   office_name: string;
//   lat: string;
//   lng: string;
//   radius: number;
//   action?: "add" | "edit" | "delete";
//   id: number | null;
// }

interface ManageLocationModalProps
  extends ModalComponentProps<any, "ADD_LOCATION" | "UPDATE_LOCATION"> {
  handleManage: (val: any, idx?: number) => void;
}

const defaultData = {
  lat: 23.719800220780733,
  lng: 90.39017821904588,
  radius: 15,
  name: "Dhaka",
};

declare global {
  interface Window {
    google: any;
  }
}

export default function LocationModal({
  handleClose,
  handleManage,
  data,
  type,
  ...rest
}: ManageLocationModalProps) {
  const [location, setLocation] = useState<{ lat: number; lng: number }>(
    defaultData,
  );
  const [radius, setRadius] = useState<number>(defaultData.radius);
  const [googleLoaded, setGoogleLoaded] = useState(false);

  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);
  const markerInstance = useRef<google.maps.Marker | null>(null);
  const circleInstance = useRef<google.maps.Circle | null>(null);

  const [form] = Form.useForm();

  useEffect(() => {
    if (data) {
      const { office_name, radius } = data as any;
      form.setFieldsValue({ office_name, radius });

      // if (lat && lng) setLocation({ lat: Number(lat), lng: Number(lng) });
      if (radius) setRadius(Number(radius));
    }
  }, [data, form]);

  const loadGoogleMapsScript = () => {
    if (window.google) {
      setGoogleLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`;
    script.async = true;
    script.onload = () => {
      setGoogleLoaded(true);
    };
    document.body.appendChild(script);
  };

  const initializeMap = () => {
    if (!mapRef.current || !window.google) return;

    const center = new window.google.maps.LatLng(location.lat, location.lng);

    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 16,
      // zoomControl: true,
      // zoomControlOptions: {
      //   position: window.google.maps.ControlPosition.RIGHT_BOTTOM,
      // },
      mapTypeControl: false,
      streetViewControl: false,
    });

    const marker = new window.google.maps.Marker({
      position: center,
      map,
      draggable: true,
    });

    const circle = new window.google.maps.Circle({
      strokeColor: "#FF0000",
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: "#FF0000",
      fillOpacity: 0.35,
      map,
      center,
      radius,
    });

    marker.addListener("dragend", (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        setLocation({ lat, lng });
        circle.setCenter({ lat, lng });
      }
    });

    // 🔍 Autocomplete setup
    const input = document.getElementById("autocomplete") as HTMLInputElement;
    const autocomplete = new window.google.maps.places.Autocomplete(input);
    autocomplete.bindTo("bounds", map);

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();

      if (!place.geometry || !place.geometry.location) return;

      const newLatLng = place.geometry.location;
      map.setCenter(newLatLng);
      map.setZoom(16);

      const lat = newLatLng.lat();
      const lng = newLatLng.lng();
      setLocation({ lat, lng });

      marker.setPosition(newLatLng);
      circle.setCenter(newLatLng);
    });

    // 👇 Listen for manual input (lat,lng)
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        const value = input.value.trim();
        const latLngRegex = /^(-?\d+(\.\d+)?),\s*(-?\d+(\.\d+)?)$/;

        if (latLngRegex.test(value)) {
          const [latStr, lngStr] = value.split(",");
          const lat = parseFloat(latStr);
          const lng = parseFloat(lngStr);

          const newLatLng = new window.google.maps.LatLng(lat, lng);
          map.setCenter(newLatLng);
          map.setZoom(16);
          setLocation({ lat, lng });

          marker.setPosition(newLatLng);
          circle.setCenter(newLatLng);
        }
      }
    });

    mapInstance.current = map;
    markerInstance.current = marker;
    circleInstance.current = circle;
  };

  const updateRadius = (newRadius: number) => {
    setRadius(newRadius);
    circleInstance.current?.setRadius(newRadius);
  };

  useEffect(() => {
    loadGoogleMapsScript();
  }, []);

  // useEffect(() => {
  //   // if (googleLoaded) {
  //   //   initializeMap();
  //   // }
  //   if (!googleLoaded) return;
  //   const timeout = setTimeout(() => {
  //     if (mapRef.current) {
  //       initializeMap();
  //     }
  //   }, 100); // wait 100ms to ensure div is rendered
  //   return () => clearTimeout(timeout);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [googleLoaded, mapRef.current]);

  useEffect(() => {
    if (!googleLoaded || !rest.open) return;

    // Use requestAnimationFrame to ensure div is mounted
    requestAnimationFrame(() => {
      if (mapRef.current) {
        initializeMap();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleLoaded, rest.open]);

  const handleRadiusChange = (val: number | null) => {
    const newRadius = val ?? 0;
    if (!isNaN(newRadius)) {
      updateRadius(newRadius);
      form.setFieldsValue({ radius: newRadius });
    }
  };

  const onFinish = (values: any) => {
    const actionType = type === "ADD_LOCATION" ? "add" : "edit";

    handleManage(
      {
        ...values,
        lat: location.lat,
        lng: location.lng,
        id: data?.data?.id,
        action: actionType,
      },
      data?.idx,
    );

    toast(
      "success",
      `Successfully ${actionType === "add" ? "Added" : "Updated"}!`,
    );
    handleClose();
  };

  return (
    <CustomModal
      title={type === "UPDATE_LOCATION" ? "Update Location" : "Add Location"}
      {...rest}
      handleClose={handleClose}
      width={700}
      footer={null}
    >
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        <div className="grid md:grid-cols-2 gap-x-4">
          <Form.Item
            name="office_name"
            label="Name"
            className="mb-4"
            rules={[{ required: true, message: "Please enter name" }]}
          >
            <Input size="large" placeholder="Ex: Banani B Building" />
          </Form.Item>

          <Form.Item
            name="radius"
            label="Radius (meter)"
            className="mb-4"
            rules={[
              {
                validator: async (_: any, value: number) => {
                  if (!value) return Promise.reject("Please enter radius");
                  if (value < 15)
                    return Promise.reject("Radius must be minimum 15 meters");
                },
              },
            ]}
            initialValue={defaultData.radius}
          >
            <InputNumber
              type="number"
              size="large"
              placeholder="Ex: 150"
              style={{ width: "100%" }}
              onChange={handleRadiusChange}
              //   disabled={loading}
            />
          </Form.Item>
        </div>

        {!googleLoaded ? (
          <Loader />
        ) : (
          <div className="relative">
            <div
              ref={mapRef}
              className="google-map"
              style={{
                width: "100%",
                height: "300px",
                marginTop: "16px",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            />
            <div className="absolute top-2 left-2 z-10">
              <Input
                id="autocomplete"
                placeholder="Search location"
                size="middle"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
              />
            </div>
          </div>
        )}

        <Space className="w-full mt-6 mb-1.5" size={12}>
          <Button
            className="normal-dark-button modal-button"
            onClick={handleClose}
            size="middle"
          >
            Cancel
          </Button>
          <Button
            className="modal-button"
            type="primary"
            htmlType="submit"
            size="middle"
            // disabled={loading}
          >
            {type === "UPDATE_LOCATION" ? "Update" : "Add"}
          </Button>
        </Space>
      </Form>
    </CustomModal>
  );
}
